import {
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  styled,
  type CardProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Provider, type SourceAccount } from '@repo/api-gw-sdk';

import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';
import { NativeVaultColor } from '@/theme/colorSchemes';

import { useVaultDetailsDialog } from './useVaultDetailsDialog';

import { OptionMenu } from '../option-menu/option-menu';

const VaultCard = ({
  clickable,
  selected,
  children,
  data,
  editable,
  onChange,
  onExternalOpenDialog,
  vaultAccount,
  sx,
  ...props
}: {
  clickable: boolean;
  selected?: boolean;
  data?: BackupVault;
  editable: boolean;
  onExternalOpenDialog?: () => void;
  onChange?: () => void;
  vaultAccount?: SourceAccount;
} & CardProps) => {
  return (
    <Card
      {...props}
      sx={[
        (theme) => ({
          backgroundColor: 'var(--mui-palette-background-primary)',
          ...theme.typography.body2,
          textAlign: 'left',
          minWidth: '300px',
          maxWidth: '420px',
          minHeight: '172px',
          boxShadow: 'none',
          padding: '24px',
          outline: selected
            ? '3px solid var(--mui-palette-text-primary)'
            : clickable
              ? '1px solid transparent'
              : 'none',
          borderRadius: '16px',
          '& .MuiTypography-root': {
            fontSize: '12px',
            color: 'var(--mui-palette-custom-BLACK-01)',
          },
          '& .MuiCardHeader-root': {
            padding: 0,
            marginBottom: '12px',
          },
          '& .MuiCardContent-root': {
            padding: 0,
          },
          cursor: clickable ? 'pointer' : 'default',
          '&:hover': {
            outline: selected
              ? '3px solid var(--mui-palette-text-primary)'
              : clickable
                ? '1px solid var(--mui-palette-primary-main)'
                : 'none',
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Card>
  );
};

const VaultTableCell = styled(TableCell)(() => ({
  borderBottom: 'none',
  color: 'var(--mui-palette-text-secondary)',
  padding: '4px 10px 4px 0px',
}));

const VaultTablePropCell = styled(VaultTableCell)(() => {
  return {
    minWidth: '120px',

    '& p': {
      fontWeight: '500',
    },
  };
});

export const VaultCardAsAccount = ({
  cloudProvider,
  region,
  sx,
  loading,
  onExternalOpenDialog,
}: {
  cloudProvider: Provider;
  region: string;
  sx?: SxProps<Theme>;
  loading?: boolean;
  onExternalOpenDialog?: () => void;
}) => {
  const accountLabel =
    cloudProvider === Provider.Azure
      ? 'subscription'
      : cloudProvider === Provider.Gcp
        ? 'project'
        : 'account';

  const title = `Source ${accountLabel}`;
  return (
    <VaultCard
      editable={false}
      sx={{
        backgroundColor: NativeVaultColor,
        ...sx,
      }}
      clickable={false}
    >
      <VaultCardHeader
        loading={loading}
        title={title}
        action={
          onExternalOpenDialog ? (
            <OptionMenu
              options={[
                {
                  id: 'see-details',
                  text: 'See vault details',
                  menuItemProps: {
                    onClick: onExternalOpenDialog,
                  },
                },
              ]}
            />
          ) : null
        }
      />
      <VaultCardContent
        loading={loading}
        cloudProvider={cloudProvider}
        type='provider-managed'
        region={region}
      />
    </VaultCard>
  );
};

export function BackupVaultCard(props: {
  data?: BackupVault;
  loading?: boolean;
  sx?: SxProps<Theme>;
  editable: boolean;
  onChange?: () => void;
  onClick?: (vault: BackupVault) => void;
  clickable?: boolean;
  selected?: boolean;
  onExternalOpenDialog?: () => void;
}) {
  const vaultDetailsDialog = useVaultDetailsDialog(props.loading);

  const handleOpenDetails = () => {
    if (props.data) {
      vaultDetailsDialog.open(props.data, {
        editable: props.editable,
        onSaveSuccess: props.onChange,
      });
    }
  };

  const id = props.loading
    ? 'loading-vault-card'
    : props.selected
      ? 'backup-vault-card-selected'
      : 'backup-vault-card';

  return (
    <VaultCard
      editable={props.editable}
      selected={props.selected}
      clickable={props.onClick !== undefined}
      onClick={() => props.onClick?.(props.data!)}
      data-testid={id}
      sx={{
        backgroundColor: props?.data?.backgroundColor,
        ...props.sx,
      }}
    >
      <VaultCardHeader
        loading={props.loading}
        title={props.data?.name || ''}
        action={
          <OptionMenu
            options={[
              {
                id: 'edit-vault',
                text: 'See vault details',
                menuItemProps: {
                  onClick: handleOpenDetails,
                },
              },
            ]}
          />
        }
      />
      <VaultCardContent
        loading={props.loading}
        cloudProvider={props.data?.vaultAttributes.cloudProvider || ''}
        type={props.data?.isManagedByEon ? 'eon-managed' : 'self-managed'}
        region={props.data?.region || ''}
      />
    </VaultCard>
  );
}

const VaultCardHeader = ({
  title,
  action,
  loading,
}: {
  title: string;
  action: React.ReactNode;
  loading?: boolean;
}) => {
  if (loading) {
    return <CardHeader component={Skeleton} />;
  }

  return (
    <CardHeader
      sx={{ paddingBottom: '5px' }}
      title={title}
      slotProps={{
        title: {
          variant: 'body1',
          sx: {
            fontSize: '12px',
            fontWeight: 'bold',
          },
        },
      }}
      action={action}
    />
  );
};

const VaultCardContent = ({
  cloudProvider,
  type,
  region,
  loading,
}: {
  cloudProvider: string;
  type: 'eon-managed' | 'self-managed' | 'provider-managed';
  region: string;
  loading?: boolean;
}) => {
  if (loading) {
    return (
      <CardContent>
        <table className='w-full table-fixed'>
          <tbody>
            {[...Array(4)].map((_, index) => (
              <tr key={index}>
                <td className='px-[5px] pt-[5px] pl-0'>
                  <Skeleton />
                </td>
                <td className='pl-[5px] pt-[5px] pr-0'>
                  <Skeleton />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    );
  }

  return (
    <CardContent>
      <TableContainer>
        <Table className='table-fixed'>
          <tbody>
            <TableRow>
              <VaultTablePropCell>
                <Typography>Cloud</Typography>
              </VaultTablePropCell>
              <VaultTableCell>
                <Typography>
                  {CloudProviders[cloudProvider]?.displayName}
                </Typography>
              </VaultTableCell>
            </TableRow>
            <TableRow>
              <VaultTablePropCell>
                <Typography>Type</Typography>
              </VaultTablePropCell>
              <VaultTableCell>
                <Typography className='truncate'>
                  {type === 'eon-managed'
                    ? 'Eon-managed'
                    : type === 'provider-managed'
                      ? 'Provider-managed'
                      : 'Self-managed'}
                </Typography>
              </VaultTableCell>
            </TableRow>
            <TableRow>
              <VaultTablePropCell>
                <Typography>Region</Typography>
              </VaultTablePropCell>
              <VaultTableCell>
                <Typography>{region || '--'}</Typography>
              </VaultTableCell>
            </TableRow>
          </tbody>
        </Table>
      </TableContainer>
    </CardContent>
  );
};
